/* eslint-disable react-hooks/exhaustive-deps */
import './index.less';

import {
  Box,
  Button,
  Grid,
  Icon,
  Image,
  Stack,
  Text,
  TextLabel,
  useColorMode,
  useColorStyle,
  ButtonProps,
} from '@tonic-one/react';
import { CloseIcon } from '@tonic-one/react-icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReportApi } from '@Apis';
import { FileInfo } from '../../../types/report';
import SmartTooltip from '../SmartTooltip';
interface Props {
  acceptTypes?: string;
  limitKb?: number;
  needPreview?: boolean;
  variant?: ButtonProps['variant'];
  // fileError='',
  // setFileError=() => {},
  fileInfo: FileInfo;
  setFileInfo: any;
  withEditPermission: boolean;
}

export default function UploadLogo({
  acceptTypes = 'image/jpeg,image/jpg,image/png',
  limitKb = 200,
  variant = 'secondary',
  fileInfo,
  setFileInfo,
  withEditPermission,
}: Props) {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const uploadInput: any = useRef(null);
  const [previewImg, setPreviewImg] = useState<any>(null);

  const sizeToKbString = useMemo(() => {
    return Math.ceil((fileInfo.fileSize || 0) / 1024) + 'K';
  }, [fileInfo?.fileSize]);

  const handleFile = async (fileList: any) => {
    const file = fileList[0];
    // console.log('file', file);
    const info = {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      fileData: file,
    };

    if (acceptTypes.indexOf(file.type) < 0 || file.type === '') {
      // fileError = 'type'
      setFileInfo({
        ...info,
        fileData: null,
        fileError: 'type',
      });
      setPreviewImg(null);
      return;
    }
    const sizeToKb = Math.ceil(file.size / 1024);
    if (sizeToKb > limitKb) {
      setFileInfo({
        ...info,
        fileData: null,
        fileError: 'size',
      });
      setPreviewImg(null);
      return;
    }
    setFileInfo({
      ...info,
      fileError: '',
    });
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (ev: any) => {
      const base64Url = ev.target.result;
      setPreviewImg(base64Url);
    };
  };

  const deleteFile = () => {
    setFileInfo({ fileError: '' });
    setPreviewImg(null);
  };

  const queryFile = async (storageId: string) => {
    const fileRes = (await ReportApi.getLogo(storageId)).data;
    setPreviewImg(`data:image/jpeg;base64,${fileRes}`);
  };

  useEffect(() => {
    if (!fileInfo.fileName) deleteFile();
    if (!fileInfo.storageId) return;
    queryFile(fileInfo.storageId);
  }, [fileInfo?.storageId, fileInfo?.fileName]);

  return (
    <Stack pt="2x" direction="column">
      <TextLabel>
        {t('reportCommon.tips.hints.uploadImageInfo1', {
          LIMITSIZE: limitKb,
        })}
      </TextLabel>
      <TextLabel mb="10px">
        {t('reportCommon.tips.hints.uploadImageInfo2')}
      </TextLabel>
      <Button
        width="min-content"
        id="uploadLogoButton"
        variant={variant}
        onClick={() => {
          uploadInput?.current.click();
        }}
        disabled={!withEditPermission}
      >
        {t('reportCommon.buttons.selectFile')}
      </Button>
      {fileInfo.fileName && (
        <Box
          className="fileReview"
          backgroundColor={colorStyle.background.tertiary}
        >
          <Grid templateColumns="auto 16px">
            <SmartTooltip>
              <Text>{`${fileInfo.fileName}(${sizeToKbString})`}</Text>
            </SmartTooltip>
            <Icon
              className="deleteFileIcon"
              _hover={{ backgroundColor: colorStyle.background.fourth }}
              as={CloseIcon}
              onClick={() => {
                deleteFile();
              }}
            ></Icon>
          </Grid>
          {fileInfo.fileError === 'type' && (
            <Text className="errorInfo">
              {t('reportCommon.tips.errors.invalidFileType')}
            </Text>
          )}
          {fileInfo.fileError === 'size' && (
            <Text className="errorInfo">
              {t('reportCommon.tips.errors.invalidFileSize')}
            </Text>
          )}
          {previewImg && <Image className="fileReviewImage" src={previewImg} />}
        </Box>
      )}
      {fileInfo.fileError === 'required' && (
        <Text className="errorInfo">
          {t('reportCommon.tips.errors.requiredField')}
        </Text>
      )}
      {/* the true button to upload file */}
      <input
        ref={uploadInput}
        type="file"
        accept={acceptTypes}
        onChange={(e) => {
          handleFile(e.target.files);
        }}
        onClick={(e: any) => {
          e.target.value = null;
        }}
        hidden
      ></input>
    </Stack>
  );
}
